import { useEffect, useState, useCallback } from 'react';
import { getFacadeSystem, saveFacadeSystem } from './api';

const emptyRow = () => ({ elevation: '', type: '', connection: '', notes: '' });

export function useFacadeStore(buildingId) {
  const [rows, setRows] = useState([emptyRow()]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [lastSavedAt, setLastSavedAt] = useState(null);

  const load = useCallback(async () => {
    if (!buildingId) return;
    setLoading(true);
    setError('');
    try {
      const data = await getFacadeSystem(buildingId);
      setRows((data?.rows && Array.isArray(data.rows) && data.rows.length > 0) ? data.rows : [emptyRow()]);
    } catch (e) {
      setError(e?.message || 'Failed to load facade system');
    } finally {
      setLoading(false);
    }
  }, [buildingId]);

  useEffect(() => {
    load();
  }, [load]);

  const updateCell = useCallback((index, key, value) => {
    setRows(prev => {
      const next = [...prev];
      const row = { ...(next[index] || emptyRow()) };
      row[key] = value;
      next[index] = row;
      return next;
    });
  }, []);

  const addRow = useCallback(() => {
    setRows(prev => [...prev, emptyRow()]);
  }, []);

  const removeRow = useCallback((index) => {
    setRows(prev => prev.filter((_, i) => i !== index));
  }, []);

  const save = useCallback(async () => {
    if (!buildingId) return { success: false };
    setLoading(true);
    setError('');
    try {
      const payload = { rows: rows.map(r => ({
        elevation: r.elevation || '',
        type: r.type || '',
        connection: r.connection || '',
        notes: r.notes || '',
      })) };
      const res = await saveFacadeSystem(buildingId, payload);
      if (!res.success) throw new Error(res.error || 'Save failed');
      setLastSavedAt(Date.now());
      return { success: true };
    } catch (e) {
      setError(e?.message || 'Failed to save facade system');
      return { success: false, error: e?.message };
    } finally {
      setLoading(false);
    }
  }, [buildingId, rows]);

  return {
    rows,
    loading,
    error,
    lastSavedAt,
    refresh: load,
    updateCell,
    addRow,
    removeRow,
    save,
  };
}